<script lang="ts" setup>
import axios from 'axios'
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { notify } from '@kyvg/vue3-notification'

import { ResourceResponse, uuid } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import { image as imageHelper } from '@/utils/assets'
import { MinimalTransaction } from '@/types/transaction'
import { emitter } from '@/utils/mitt'
import useForm from '@/hooks/use-form'

import ContentHeading from '@/components/layout/ContentHeading.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MyLabeledField from '@/components/my-components/MyLabeledField.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyRadioButton from '@/components/my-components/form/MyRadioButton.vue'
import MyRadioButtonGroup from '@/components/my-components/form/MyRadioButtonGroup.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'

interface Props {
    id: uuid
}

interface TransitProductDifferenceAlarm {
    id: uuid
    destinationTransaction: MinimalTransaction
    transitTransaction: MinimalTransaction
}

interface CombinedProduct {
    id: uuid
    name: string
    image: string
    transitAmount: number
    destinationAmount: number
}

interface Form {
    confirmDestinationTransaction: boolean | null
    updateTransitTransaction: boolean
}

const props = defineProps<Props>()
const open = defineModel<boolean>('open', { default: true })

const authStore = useAuthStore()
const { t } = useI18n()

const form = useForm<Form>({
    confirmDestinationTransaction: null,
    updateTransitTransaction: false,
})
const alarm = ref<TransitProductDifferenceAlarm | null>(null)
const loading = ref(false)

const sender = computed(() => {
    if (!alarm.value) return ''

    return `${alarm.value.destinationTransaction.sendingCompany.name} - ${alarm.value.destinationTransaction.sendingLocation.name}`
})
const receiver = computed(() => {
    if (!alarm.value) return ''

    return `${alarm.value.destinationTransaction.receivingCompany.name} - ${alarm.value.destinationTransaction.receivingLocation.name}`
})

const products = computed(() => {
    if (!alarm.value) return []

    const products: Record<uuid, CombinedProduct> = {}

    for (const product of alarm.value.transitTransaction.products) {
        products[product.id] = {
            id: product.id,
            name: product.name,
            image: product.image,
            transitAmount: product.delivered,
            destinationAmount: 0,
        }
    }

    for (const product of alarm.value.destinationTransaction.products) {
        if (product.id in products) {
            products[product.id].destinationAmount = product.delivered
        } else {
            products[product.id] = {
                id: product.id,
                name: product.name,
                image: product.image,
                transitAmount: 0,
                destinationAmount: product.delivered,
            }
        }
    }

    return Object.values(products)
})

async function fetchAlarm() {
    loading.value = true
    try {
        const response = await axios.get<ResourceResponse<TransitProductDifferenceAlarm>>(
            window.route('packaging.company.handle-transit-product-difference.show', {
                company: authStore.companyId,
                alarm: props.id,
            }),
        )
        alarm.value = response.data.data
    } finally {
        loading.value = false
    }
}

async function handleAlarm() {
    if (!alarm.value) return

    const response = await form.submit(
        'PUT',
        window.route('packaging.company.handle-transit-product-difference.update', [
            authStore.companyId,
            alarm.value.id,
        ]),
    )

    if (response) {
        emitter.emit('fetchAlarms')
        notify({ title: t('alarmResolved'), type: 'success' })
        open.value = false
    }
}

onBeforeMount(fetchAlarm)
watch(() => props.id, fetchAlarm)
</script>

<template>
    <MyModal v-model="open" :max-width="700">
        <LoaderWrapper :visible="loading || form.loading.value" />

        <div v-if="alarm" class="flex flex-col space-y-4">
            <ContentHeading>
                {{ t('handleProductDifference') }}
            </ContentHeading>

            <div class="flex space-x-4">
                <MyLabeledField :title="t('sender')" :content="sender" />
                <MyLabeledField :title="t('receiver')" :content="receiver" />
            </div>
            <MyLabeledField
                :title="t('comment')"
                :content="alarm.destinationTransaction.details?.note"
            />

            <MyLabeledField :title="t('product')">
                <MyPanel bordered panel-class="overflow-hidden mt-2">
                    <table class="w-full">
                        <thead>
                            <tr
                                class="border-b border-primary-200 bg-primary-100 text-xs dark:border-dark-400 dark:bg-dark-600"
                            >
                                <th class="p-2.5 font-semibold" colspan="2" v-text="t('name')" />
                                <th
                                    class="w-20 border-x border-primary-200 p-2.5 font-semibold dark:border-dark-400"
                                    v-text="t('originalAmount')"
                                />
                                <th class="w-20 p-2.5 font-semibold" v-text="t('adjustedAmount')" />
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="product in products"
                                :key="product.id"
                                class="border-t border-primary-200 first:border-t-0 even:bg-primary-100 dark:border-dark-400 dark:even:bg-dark-600"
                            >
                                <td class="w-[60px]">
                                    <img
                                        :src="imageHelper(product.image, 'thumbnail')"
                                        :alt="product.name"
                                    />
                                </td>
                                <td
                                    class="border-r border-primary-200 p-2.5 dark:border-dark-400"
                                    v-text="product.name"
                                />
                                <td
                                    class="border-r border-primary-200 text-center dark:border-dark-400"
                                >
                                    {{ product.transitAmount }}
                                </td>
                                <td class="text-center">
                                    {{ product.destinationAmount }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </MyPanel>
            </MyLabeledField>

            <MyForm
                class="flex flex-col space-y-4"
                :errors="form.errors.value"
                @submit.prevent="handleAlarm"
            >
                <MyRadioButtonGroup
                    v-model="form.data.confirmDestinationTransaction"
                    class="relative"
                >
                    <template #label>
                        {{ t('howToHandleProductDifference') }}
                        <mdi:information
                            v-tooltip="t('howToHandleProductDifferenceExplanation')"
                            class="absolute top-1 right-0"
                        />
                    </template>

                    <MyRadioButton :value="true" :label="t('confirmAdjustment')" />
                    <MyRadioButton :value="false" :label="t('declineAdjustment')" />
                </MyRadioButtonGroup>

                <div
                    v-if="form.data.confirmDestinationTransaction"
                    class="flex items-center space-x-2"
                >
                    <MyCheckbox
                        v-model="form.data.updateTransitTransaction"
                        :label="t('updateStock')"
                    />
                    <mdi:information v-tooltip="t('productDifferenceUpdateStockExplanation')" />
                </div>

                <div class="mt-4 flex justify-end">
                    <MyButton
                        :disabled="
                            form.loading.value || form.data.confirmDestinationTransaction === null
                        "
                        scheme="primary"
                        v-text="t('submit')"
                    />
                </div>
            </MyForm>
        </div>

        <h1
            v-if="!alarm && !loading"
            class="my-auto text-center text-4xl font-bold"
            v-text="t('alarmNotFound')"
        />
    </MyModal>
</template>
